import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Image from '@base/Image';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import { setModalState, setLanguage } from '@actions/quizTaking';
import { getIsLoggedIn, getIsPlatformUnlocked, getMe } from '@selectors/auth';
import { getQuizDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getTimeWithoutAmPm12HrFormat, getDateShort } from '@utils/dates';
import { OPEN_IN_APP_CLICKED, START_TEST_CLICKED } from '@constants/segment';
import { useToggleable } from '@stores/toggleables';
import { useSnackbar } from '@stores/snackbar';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { unlockFreePlanFor } from '@constants/index';
import { mediaQuery, Spacings } from '@styles/index';
import useLiteUpgradeDialog from '@hooks/useLiteUpgradeDialog';
import {
  getIsSubscribed,
  getIsSubscribedToIconic,
  getIsSubscribedToLite
} from '@selectors/subscription';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import { getIsGTPGoal, getIsTestRevampGoal } from '@api/selectors/constants';
import pushRoute from '@utils/pushRoute';
import { useIsMobile } from '@stores/AppCommonData';
import useConstants from '@api/hooks/useConstants';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { STORE_FLAG } from '@cont/Store/TestSeries/FullSchedule/constants';
import Dynamic from '@base/Dynamic/Dynamic';
import Skeleton from 'aquilla/core/atoms/Skeleton';
import useTotalQuestions from '@cont/Quiz/Common/useTotalQuestions';
import useEnroll from '../../../Common/useEnroll';
import useQuizStatus from '../../Common/useQuizStatus';
import useQuizSegmentAnalytics from '../../../Common/useQuizSegmentAnalytics';
import { MODAL_TYPES } from '../../../constants';
import { getIsGeneralInstructions } from '../../utils';
import useTestAccess from './useSkuAccessForTest';

const FreePlanUnlock = Dynamic(() =>
  import('@comp/FreePlanUnlock/FreePlanUnlock')
);
const LiteUpgradeDialog = Dynamic(() =>
  import('@cont/Course/CourseHeader/LiteUpgradeDialog')
);

const LightningIcon = 'quiz/lightning-white.svg';
const FileIcon = 'quiz/file.svg';

export const Wrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_5B};
  margin-top: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoMobile} {
    flex-flow: column;
    text-align: center;
  }
`;

export const StyledButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const DefaultActions = () => {
  const dispatch = useDispatch();
  const { query, pathname } = useRouter();
  const { quizUID, quizSlug, source } = query;

  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const isLoggedIn = useSelectorWithShallowEqual(getIsLoggedIn);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const goalUID = quizDetails.goal_uid;
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUID));

  const goalData = useSelector(
    (state) => state.topologies.data[quizDetails.goal_uid]
  );
  const segmentAnalytics = useQuizSegmentAnalytics();
  const { isHeldInFuture, toBeHeldToday, isLive, hasAttempted } =
    useQuizStatus();
  const isMobile = useIsMobile();
  const me = useSelectorWithShallowEqual(getMe);
  const isPlatformUnlocked = useSelector(getIsPlatformUnlocked);
  const isSubscribed = useSelector(getIsSubscribed) || isLiteUser;
  const isPlusUser = hasActiveSubscriptionToGoal(me, goalUID);
  const isIconicUser = useSelector(
    getIsSubscribedToIconic(quizDetails.goal_uid)
  );
  const isSpecial = quizDetails.collection?.programme?.is_special;
  const { checkIfUserHasSubscription } = useEnroll();
  const {
    showLiteUpgradeDialog,
    openLiteUpgradeDialog,
    closeLiteUpgradeDialog
  } = useLiteUpgradeDialog();
  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const { open: showSnackbar } = useSnackbar();
  const isRevampExp = useConstants(getIsTestRevampGoal(goalUID));
  const isGeneralIstructions = getIsGeneralInstructions(
    quizDetails.instructions
  );

  const totalQuestions = useTotalQuestions();
  const generatedAppLink = useOneLinkSmartScriptV2({
    campaign: 'quiz page'
  });

  const isSubscribedUser = isIconicUser || isPlusUser || isLiteUser;

  const isEnrollDisabled = isHeldInFuture || toBeHeldToday;
  const handleGetSubscription = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalData.goalSlug}/${goalUID}/subscribe`
    );
  };

  const { isTestAccessPresent, refetchUam, isUamValidating } = useTestAccess(
    quizUID,
    'Quiz:Join',
    'Quiz',
    isSubscribedUser,
    isSpecial
  );

  useEffect(() => {
    if (isLoggedIn) {
      refetchUam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  const isFromStore = source === STORE_FLAG;
  const hasAccessFromStore = !isSubscribedUser && isTestAccessPresent;

  const hideTestCTA =
    isLoggedIn &&
    isFromStore &&
    !isPlusUser &&
    !isSpecial &&
    !isTestAccessPresent;

  const getLabel = () => {
    if (isMobile) {
      return 'Open In App';
    }

    if (isGTP && !isPlusUser && !isSpecial && !hasAccessFromStore) {
      return 'Get Subscription';
    }

    if (isHeldInFuture) {
      return `Starts on ${getDateShort(quizDetails.start_time)}`;
    }

    if (toBeHeldToday) {
      if (isGTP) {
        return `Starts on ${getDateShort(quizDetails.start_time)}`;
      }
      return `Join test at ${getTimeWithoutAmPm12HrFormat(
        quizDetails.start_time
      )}`;
    }

    if (isLive) {
      return 'Join test';
    }

    if (!hasAttempted) {
      return 'Take test';
    }

    return 'Reattempt test';
  };

  const getVariant = () => {
    if (isMobile) {
      return 'filled';
    }

    if (!totalQuestions) {
      return 'disabled';
    }

    return isEnrollDisabled ? 'disabled' : 'filled';
  };

  const showInstructions = (canEnroll = false) => {
    const type =
      isRevampExp && isGeneralIstructions
        ? MODAL_TYPES.REVAMPED_INSTRUCTIONS_DRAWER
        : MODAL_TYPES.QUIZ_INSTRUCTION;

    dispatch(
      setModalState({
        type,
        data: {
          canEnroll
        }
      })
    );
  };

  const openLanguage = () => {
    dispatch(
      setModalState({
        type: MODAL_TYPES.QUIZ_LANGUAGE_SELECTION,
        data: {}
      })
    );
  };

  const sendAnalyticsOnEnroll = () => {
    segmentAnalytics(START_TEST_CLICKED, {
      is_invited: !!query?.referral_code,
      fpu_invite_code: query?.referral_code
    });
  };

  const handleOnEnrollForMobile = async () => {
    window.open(generatedAppLink, '_blank');
  };

  const enrollAction = async () => {
    sendAnalyticsOnEnroll();
    try {
      await checkIfUserHasSubscription(hasAccessFromStore);

      const hasMultipleLanguages = quizDetails.available_language.length > 1;

      if (hasMultipleLanguages) {
        openLanguage();
      } else {
        dispatch(setLanguage(quizDetails.available_language[0]));

        if (isRevampExp && isGeneralIstructions) {
          return pushRoute(
            {
              pathname,
              query: {
                quizUID,
                quizSlug,
                show_instructions: true
              }
            },
            undefined,
            { shallow: true }
          );
        }

        showInstructions(true);
      }
    } catch (err) {
      showSnackbar({
        message: err.message,
        type: 'error'
      });
    }
  };

  const handleOnEnrollClick = () => {
    if (isLiteUser && !quizDetails?.is_allowed_for_lite) {
      openLiteUpgradeDialog();
      return;
    }

    if (isMobile) {
      segmentAnalytics(OPEN_IN_APP_CLICKED, {
        is_invited: !!query?.referral_code,
        fpu_invite_code: query?.referral_code
      });
      handleOnEnrollForMobile();
      return;
    }

    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (isLoggedIn && !isSubscribed && !isPlatformUnlocked) {
      setFreePlanUnlockDrawerState(true);
      return;
    }

    if (isGTP && !isPlusUser && !isSpecial && !hasAccessFromStore) {
      return handleGetSubscription();
    }

    enrollAction();
  };

  const upgradeSubscription = (e) => {
    e.stopPropagation();
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalData.goalSlug}/${goalData.uid}/subscribe`,
      {},
      'quiz'
    );
  };

  const handleOnInstructionsClick = () => showInstructions();
  const closeFreePlanUnlockedDrawer = () => setFreePlanUnlockDrawerState(false);
  const freePlanUnlockedCallback = () => {
    setTimeout(() => {
      closeFreePlanUnlockedDrawer();
      enrollAction();
    }, 3000);
  };

  const renderPrimaryCta = () => {
    if (hideTestCTA) {
      return null;
    }
    if (isUamValidating) {
      return <Skeleton width={176} height={48} type="button" />;
    }

    return (
      <StyledButton
        label={getLabel()}
        icon={
          isLive ? (
            <Image width={30} height={30} src={LightningIcon} alt="live-icon" />
          ) : null
        }
        variant={getVariant()}
        onClick={handleOnEnrollClick}
      />
    );
  };

  const renderSecondaryCta = () => {
    if (hideTestCTA || !isLive) {
      return (
        <StyledButton
          icon={
            <Image height={24} width={24} src={FileIcon} alt="instruction" />
          }
          label="Instructions"
          variant="hollow"
          color="black"
          onClick={handleOnInstructionsClick}
        />
      );
    }

    return null;
  };

  return (
    <>
      <OneLinkSmartScript />
      <Wrapper>
        {isMobile && <P1>Test can be taken only on the app</P1>}
        {renderPrimaryCta()}
        {renderSecondaryCta()}
        <FreePlanUnlock
          drawerState={freePlanUnlockDrawerState}
          closeDrawerHandler={closeFreePlanUnlockedDrawer}
          objectType={unlockFreePlanFor.FREE_TEST}
          objectUid={quizDetails.uid}
          callBack={freePlanUnlockedCallback}
        />
      </Wrapper>
      {showLiteUpgradeDialog && (
        <LiteUpgradeDialog
          onClose={closeLiteUpgradeDialog}
          upgradeSubscription={upgradeSubscription}
          message="Plus & Iconic learners can enroll into this test series. Also get access to notes and more self-study features."
        />
      )}
    </>
  );
};

export default DefaultActions;
